import { assign, fromPromise, setup } from 'xstate';

import { AuthProvider_UserFragment } from '@/graphql';
import {
  checkForHasuraUser,
  checkForOryUser, // TODO do this on the backend
  createUser,
  getOry,
  insertOryIdIntoUser,
  logger,
  logout,
  sendLoginOtp,
  sendRegistrationOtp,
  verifyLoginOtp,
  verifyRegistrationOtp,
} from '@/utils';

// ─── Type Definitions ───────────────────────────────────────────────────────
interface AuthMachineContext {
  oryId: string | null;
  flowId: string | null;
  userId: string | null;
  otpCode: string;
  firstName: string;
  lastName: string;
  csrfToken: string | null;
  userExistsInOry: boolean | null;
  returnUrl: string | null;
  phoneNumber: string;
  errorMessage: string | null;
  user: AuthProvider_UserFragment | null;
  userExistsInHasura: boolean | null;
}

type AuthMachineEvent =
  | { type: 'RETRY' }
  | {
      type: 'OTP_SENT';
      flowId: string;
      csrfToken: string;
      userExistsInOry: boolean;
      userId: string | null;
    }
  | { type: 'BACK' }
  | { type: 'FLOW_ERROR'; message: string }
  | { type: 'SUBMIT_PHONE'; phoneNumber: string }
  | { type: 'USER_CREATED'; success: boolean }
  | { type: 'OTP_SUBMITTED' }
  | { type: 'UPDATE_OTP'; otpCode: string }
  | { type: 'UPDATE_USER_DETAILS'; firstName: string; lastName: string }
  | { type: 'USER_DETAILS_SUBMITTED' }
  | { type: 'OTP_CHECK_COMPLETE'; success: boolean; oryId?: string };

// ─── Auth Machine ───────────────────────────────────────────────────────────
let ory = getOry();

const getAuthMachine = () => {
  return setup({
    types: {
      context: {} as AuthMachineContext,
      input: {} as AuthMachineContext,
    },
    actors: {
      /**
       * Generic action to send OTP - handles both login and registration cases
       */
      sendOtp: fromPromise(
        async ({
          input: { phoneNumber },
        }: {
          input: Partial<AuthMachineContext>;
        }) => {
          logger.debug(
            `Executing sendOtp action: ${JSON.stringify(
              {
                phoneNumber: phoneNumber,
                hasOry: !!ory,
              },
              undefined,
              2,
            )}`,
          );

          // Check requirements
          if (!phoneNumber) {
            throw new Error('Missing phone number for OTP sending');
          }

          // Get or create Ory client
          const oryClient = ory;

          // Check if user exists in the database
          const { exists: userExistsInOry } =
            await checkForOryUser(phoneNumber);

          const result = userExistsInOry
            ? await sendLoginOtp(oryClient, phoneNumber)
            : await sendRegistrationOtp(oryClient, phoneNumber);

          if (result.success && result.flowId && result.csrfToken) {
            logger.debug(`Successfully sent OTP`);

            const output = {
              flowId: result.flowId,
              csrfToken: result.csrfToken,
              userExistsInOry: userExistsInOry,
            };
            return output;
          } else {
            logger.debug(JSON.stringify(result, undefined, 2));
            logger.error(
              `Failed to send OTP: ${JSON.stringify({ error: result.error }, undefined, 2)}`,
            );
          }
        },
      ),

      /**
       * Generic function to verify OTP - handles both login and registration cases
       */
      verifyOtp: fromPromise(
        async ({
          input: { phoneNumber, otpCode, flowId, csrfToken, userExistsInOry },
        }: {
          input: Partial<AuthMachineContext>;
        }) => {
          // Check requirements
          if (!phoneNumber) {
            throw new Error('Missing phone number for OTP verification');
          }
          if (!otpCode) {
            throw new Error('Missing OTP code for verification');
          }
          if (!flowId || !csrfToken) {
            throw new Error('Missing flow data for OTP verification');
          }

          // Get or create Ory client
          const oryClient = ory;

          // Choose the verification function based on userExistsInOry flag
          const verifyFn = userExistsInOry
            ? verifyLoginOtp
            : verifyRegistrationOtp;

          logger.debug(
            `Verifying OTP: ${JSON.stringify(
              {
                phoneNumber: phoneNumber,
                userExistsInOry: userExistsInOry,
                flowId: flowId.substring(0, 8) + '...', // Log partial ID for security
              },
              undefined,
              2,
            )}`,
          );

          const result = await verifyFn(oryClient, {
            flowId: flowId,
            csrfToken: csrfToken,
            phoneNumber: phoneNumber,
            otpCode: otpCode,
          });

          logger.debug(
            `OTP verification result: ${JSON.stringify(
              {
                success: result.success,
                hasOryId: !!result.oryId,
                hasError: !!result.error,
              },
              undefined,
              2,
            )}`,
          );

          if (result.success && result.oryId) {
            const minimalOutput = {
              type: 'OTP_CHECK_COMPLETE',
              success: true,
              oryId: result.oryId,
            };

            return minimalOutput;
          } else {
            throw new Error(`Failed to verify OTP: ${result.error}`);
          }
        },
      ),

      createUser: fromPromise(
        async ({
          input: { oryId, phoneNumber, firstName, lastName },
        }: {
          input: Partial<AuthMachineContext>;
        }) => {
          try {
            // Check requirements
            if (!oryId) {
              throw new Error('Missing identity ID for account creation');
            }
            if (!phoneNumber) {
              throw new Error('Missing phone number for account creation');
            }
            if (!firstName || !lastName) {
              throw new Error('Missing name details for account creation');
            }

            // Create the user with our utility function
            // This uses Apollo Client with UserOperations_CreateUserAndRelations
            const { success, user } = await createUser({
              oryId,
              phoneNumber,
              firstName,
              lastName,
            });

            logger.debug(
              `User creation result: ${JSON.stringify(
                {
                  success,
                  user,
                },
                undefined,
                2,
              )}`,
            );

            return { user };
          } catch (error) {
            throw new Error(`Exception in createUser action: ${error}`);
          }
        },
      ),

      checkUserInHasura: fromPromise(
        async ({
          input: { phoneNumber },
        }: {
          input: Partial<AuthMachineContext>;
        }) => {
          if (!phoneNumber) {
            throw new Error('Missing phone number for user check');
          }
          const {
            exists: userExists,
            userId,
            fullUser,
          } = await checkForHasuraUser(phoneNumber);

          return { userExistsInHasura: userExists, userId, user: fullUser };
        },
      ),

      insertOryIdIntoUser: fromPromise(
        async ({
          input: { userId, oryId },
        }: {
          input: { userId: string; oryId: string };
        }) => {
          if (!userId) {
            throw new Error('Missing user ID for Ory ID insertion');
          }
          if (!oryId) {
            throw new Error('Missing Ory ID for user insertion');
          }
          const { success, user } = await insertOryIdIntoUser({
            userId,
            oryId,
          });

          logger.debug(
            `Ory ID insertion result: ${JSON.stringify(
              {
                success,
                user,
              },
              undefined,
              2,
            )}`,
          );

          return { user };
        },
      ),

      logout: fromPromise(async () => {
        return await logout(ory);
      }),
    },

    actions: {
      /**
       * Initialize Ory API client
       */
      reinitializeOry: () => {
        ory = getOry();
      },

      refresh: () => {
        if (typeof window !== 'undefined') {
          window.location.reload();
        }
      },

      resetOry: assign({
        oryId: () => null,
        flowId: () => null,
        userId: () => null,
        otpCode: () => '',
        csrfToken: () => null,
        userExistsInOry: () => null,
        userExistsInHasura: () => null,
        errorMessage: () => null,
      }),

      resetAll: assign({
        oryId: () => null,
        flowId: () => null,
        userId: () => null,
        otpCode: () => '',
        csrfToken: () => null,
        userExistsInOry: () => null,
        userExistsInHasura: () => null,
        errorMessage: () => null,
        firstName: () => '',
        lastName: () => '',
        returnUrl: () => null,
        phoneNumber: () => '',
      }),

      storeError: assign({
        errorMessage: ({ event }) => event?.error ?? event?.output?.error,
      }),

      storePhoneNumber: assign({
        phoneNumber: ({ event }) => event.phoneNumber,
      }),

      /**
       * Clear error message
       */
      clearError: assign({
        errorMessage: () => null,
      }),

      /**
       * Clear OTP code
       */
      clearOtpCode: assign({
        otpCode: () => '',
      }),

      /**
       * Store OTP in context
       */
      storeOtp: assign({ otpCode: ({ event: { otpCode } }) => otpCode }),

      /**
       * Store OTP flow data in context
       */
      storeFlowData: assign({
        flowId: ({
          event: {
            output: { flowId },
          },
        }) => flowId ?? null,
        csrfToken: ({
          event: {
            output: { csrfToken },
          },
        }) => csrfToken ?? null,
      }),

      storeUserExistsInOry: assign({
        userExistsInOry: ({
          event: {
            output: { userExistsInOry },
          },
        }) => userExistsInOry,
      }),

      storeUserExistsInHasura: assign({
        userExistsInHasura: ({
          event: {
            output: { userExistsInHasura },
          },
        }) => userExistsInHasura,
      }),

      storeUserId: assign({
        userId: ({
          event: {
            output: { userId },
          },
        }) => userId ?? null,
      }),

      /**
       * Store user details in context
       */
      storeUserDetails: assign({
        firstName: ({ event }) => ('firstName' in event ? event.firstName : ''),
        lastName: ({ event }) => ('lastName' in event ? event.lastName : ''),
      }),

      /**
       * Store identity ID in context
       */
      storeOryId: assign({
        oryId: ({
          event: {
            output: { oryId },
          },
        }) => oryId ?? null,
      }),

      storeUser: assign({
        user: ({
          event: {
            output: { user },
          },
        }) => user ?? null,
      }),
    },

    guards: {
      /**
       * Check if user doesn't exist in the system
       */

      userDoesNotExistInHasura: ({ context }) => !context.userExistsInHasura,

      userIsMissingOryIdInHasura: ({ context }) => {
        return !context.userExistsInOry && !!context.userExistsInHasura;
      },

      /**
       * Check if OTP verification was successful
       */
      otpVerified: ({ event }) => {
        if ('success' in event) {
          return event.success === true;
        }
        return false;
      },

      /**
       * Check if user creation was successful
       */
      userCreationSuccessful: ({ event }) => {
        if ('success' in event) {
          return event.success === true;
        }
        return false;
      },

      /**
       * Check if OTP code is valid
       */
      hasValidOtpCode: ({ context }) =>
        !!context.otpCode && context.otpCode?.length === 6,
    },
  }).createMachine({
    id: 'auth',
    initial: 'idle',
    context: {
      oryId: null,
      flowId: null,
      userId: null,
      otpCode: '',
      lastName: '',
      csrfToken: null,
      firstName: '',
      userExistsInOry: false,
      returnUrl: null,
      phoneNumber: '',
      errorMessage: null,
      user: null,
      userExistsInHasura: false,
    },
    states: {
      idle: {
        tags: ['phoneInput'],
        entry: {
          type: 'resetOry',
        },

        on: {
          SUBMIT_PHONE: {
            target: 'sendingOtp',
            actions: 'storePhoneNumber',
          },
        },
      },

      sendingOtp: {
        tags: ['otpInput', 'loading'],
        invoke: {
          id: 'sendOtpActor',
          src: 'sendOtp',
          input: ({ context: { phoneNumber } }) => ({
            phoneNumber,
          }),
          onDone: {
            target: 'awaitingOtpInput',
            actions: [
              {
                type: 'storeFlowData',
              },
              {
                type: 'storeUserExistsInOry',
              },
            ],
          },
          onError: {
            target: 'error',
            actions: 'storeError',
          },
        },
      },

      awaitingOtpInput: {
        tags: ['otpInput'],
        on: {
          BACK: {
            target: 'idle',
          },
          UPDATE_OTP: {
            actions: {
              type: 'storeOtp',
            },
          },
          OTP_SUBMITTED: {
            target: 'verifyingOtp',
            guard: {
              type: 'hasValidOtpCode',
            },
          },
          RETRY: {
            target: 'sendingOtp',
            actions: {
              type: 'clearOtpCode',
            },
          },
          FLOW_ERROR: {
            target: 'error',
            actions: assign({
              errorMessage: ({ event }) => event.message,
            }),
          },
        },
      },

      verifyingOtp: {
        tags: ['otpInput', 'loading'],
        invoke: {
          id: 'verifyOtpActor',
          src: 'verifyOtp',
          input: ({ context }) => context,
          onDone: {
            actions: {
              type: 'storeOryId',
            },
            target: 'checkingUser',
          },
          onError: {
            target: 'error',
            actions: 'storeError',
          },
        },
      },

      checkingUser: {
        tags: ['otpInput', 'loading'],
        invoke: {
          id: 'checkUserActor',
          src: 'checkUserInHasura',
          input: ({ context }) => context,
          onDone: {
            actions: [
              {
                type: 'storeFlowData',
              },
              {
                type: 'storeUser',
              },
              {
                type: 'storeUserId',
              },
              {
                type: 'storeUserExistsInHasura',
              },
            ],
            target: 'decidingToCollectUserDetails',
          },
          onError: {
            target: 'error',
            actions: 'storeError',
          },
        },
      },

      decidingToCollectUserDetails: {
        tags: ['otpInput', 'loading'],
        always: [
          {
            target: 'insertingOryIdIntoUser',
            guard: {
              type: 'userIsMissingOryIdInHasura',
            },
          },
          {
            target: 'collectingUserDetails',
            guard: {
              type: 'userDoesNotExistInHasura',
            },
          },
          {
            target: 'loggedIn',
          },
        ],
      },

      insertingOryIdIntoUser: {
        tags: ['otpInput', 'loading'],
        invoke: {
          id: 'insertOryIdIntoUserActor',
          src: 'insertOryIdIntoUser',
          input: ({ context: { userId, oryId } }) => ({
            userId: userId ?? '',
            oryId: oryId ?? '',
          }),
          onDone: {
            target: 'loggedIn',
            actions: [
              {
                type: 'storeUser',
              },
              {
                type: 'clearError',
              },
            ],
          },
          onError: {
            target: 'error',
            actions: 'storeError',
          },
        },
      },

      collectingUserDetails: {
        tags: ['userDetails'],
        on: {
          UPDATE_USER_DETAILS: {
            actions: {
              type: 'storeUserDetails',
            },
          },
          USER_DETAILS_SUBMITTED: {
            target: 'creatingUser',
          },
          FLOW_ERROR: {
            target: 'error',
            actions: assign({
              errorMessage: ({ event }) => event.message,
            }),
          },
        },
      },

      creatingUser: {
        tags: ['userDetails', 'loading'],
        invoke: {
          id: 'createUserActor',
          src: 'createUser',
          input: ({
            context: { oryId, phoneNumber, firstName, lastName },
          }) => ({ oryId, phoneNumber, firstName, lastName }),
          onDone: {
            target: 'loggedIn',
            actions: [
              {
                type: 'storeUser',
              },
              {
                type: 'clearError',
              },
            ],
          },
          onError: {
            target: 'error',
            actions: 'storeError',
          },
        },
      },

      loggedIn: {
        tags: ['loggedIn'],
        on: {
          LOG_OUT: {
            target: 'loggingOut',
          },
        },
      },

      loggingOut: {
        tags: ['loggedIn', 'loading'],
        invoke: {
          id: 'logoutActor',
          src: 'logout',
          onDone: {
            target: 'loggedOut',
            actions: [
              {
                type: 'resetAll',
              },
            ],
          },
          onError: {
            target: 'error',
            actions: 'storeError',
          },
        },
      },

      loggedOut: {
        tags: ['phoneInput', 'loading'],
        after: {
          100: {
            target: 'idle',
          },
        },
      },

      error: {
        tags: ['phoneInput', 'loading'],
        always: {
          target: 'idle',
          actions: ['resetOry', 'reinitializeOry'],
        },
      },
    },
  });
};

export { getAuthMachine };
export type { AuthMachineContext, AuthMachineEvent };
